import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../api/client';
import { format, differenceInDays } from 'date-fns';
import { toast } from 'sonner';
import { 
  Plus, Search, Filter, AlertTriangle, CheckCircle, Clock,
  ChevronRight, RefreshCw, Download, X, Truck, FileText,
  Timer, Calendar, User, Edit2, Save
} from 'lucide-react';

const NCRManagement = () => {
  const [ncrs, setNcrs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNcr, setSelectedNcr] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [filters, setFilters] = useState({ vendor_id: '', status: '', severity: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize edit form when NCR is selected
  useEffect(() => {
    if (selectedNcr) {
      setEditForm({
        severity: selectedNcr.severity || 'minor',
        po_number: selectedNcr.po_number || '',
        part_number: selectedNcr.part_number || '',
        quantity_received: selectedNcr.quantity_received || 0,
        quantity_defective: selectedNcr.quantity_defective || 0,
        description: selectedNcr.description || '',
        defect_type: selectedNcr.defect_type || '',
        root_cause: selectedNcr.root_cause || '',
        corrective_action: selectedNcr.corrective_action || ''
      });
    }
  }, [selectedNcr]);

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await apiClient.patch(`/ncrs/${selectedNcr.id}`, editForm);
      const response = await apiClient.get(`/ncrs/${selectedNcr.id}`);
      setSelectedNcr(response.data);
      fetchNcrs();
      setIsEditing(false);
      toast.success('NCR updated successfully');
    } catch (error) {
      console.error('Error updating NCR:', error);
      toast.error('Failed to update NCR');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditForm({
      severity: selectedNcr.severity || 'minor',
      po_number: selectedNcr.po_number || '',
      part_number: selectedNcr.part_number || '',
      quantity_received: selectedNcr.quantity_received || 0,
      quantity_defective: selectedNcr.quantity_defective || 0,
      description: selectedNcr.description || '',
      defect_type: selectedNcr.defect_type || '',
      root_cause: selectedNcr.root_cause || '',
      corrective_action: selectedNcr.corrective_action || ''
    });
    setIsEditing(false);
  };

  const fetchNcrs = useCallback(async () => {
    try {
      let url = '/ncrs';
      const params = new URLSearchParams();
      if (filters.vendor_id) params.append('vendor_id', filters.vendor_id);
      if (filters.status) params.append('status', filters.status);
      if (filters.severity) params.append('severity', filters.severity);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await apiClient.get(url);
      setNcrs(response.data);
    } catch (error) {
      console.error('Error fetching NCRs:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchVendors = async () => {
    try {
      const response = await apiClient.get('/vendors');
      setVendors(response.data);
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  };

  useEffect(() => {
    fetchNcrs();
    fetchVendors();
  }, [fetchNcrs]);

  const getDaysOpen = (createdAt) => {
    if (!createdAt) return 0;
    return differenceInDays(new Date(), new Date(createdAt));
  };

  const getAgingColor = (days) => {
    if (days > 30) return 'text-red-600 bg-red-50';
    if (days > 14) return 'text-orange-600 bg-orange-50';
    if (days > 7) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const createCapa = async (ncr) => {
    try {
      const capaData = {
        capa_number: '',
        title: `CAPA for ${ncr.ncr_number}`,
        capa_type: 'corrective',
        source_type: 'ncr',
        source_id: ncr.id,
        source_reference: ncr.ncr_number,
        department: 'Quality Control',
        department_code: '0600',
        problem_statement: ncr.description,
        severity: ncr.severity,
        owner_id: 'current-user',
        owner_name: 'Current User',
        due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        created_by: 'current-user',
        created_by_name: 'Current User'
      };
      
      await apiClient.post('/enhanced-capas', capaData);
      alert('CAPA created successfully!');
      fetchNcrs();
    } catch (error) {
      console.error('Error creating CAPA:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="ncr-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">NCR Management</h1>
          <p className="text-sm sm:text-base text-slate-500">Vendor Non-Conformance Reports</p>
        </div>
        <button
          onClick={() => setShowNewModal(true)}
          className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm sm:text-base w-full sm:w-auto"
        >
          <Plus className="h-4 w-4" />
          New NCR
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
          <Filter className="h-4 w-4 text-slate-400 hidden sm:block" />
          <select
            value={filters.vendor_id}
            onChange={(e) => setFilters({ ...filters, vendor_id: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm flex-1 sm:flex-none"
          >
            <option value="">Vendor</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>{vendor.vendor_code}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm flex-1 sm:flex-none"
          >
            <option value="">Status</option>
            <option value="open">Open</option>
            <option value="under_review">Review</option>
            <option value="pending_vendor">Pending</option>
            <option value="closed">Closed</option>
          </select>
          <select
            value={filters.severity}
            onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
            className="px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg text-xs sm:text-sm flex-1 sm:flex-none"
          >
            <option value="">Severity</option>
            <option value="critical">Critical</option>
            <option value="major">Major</option>
            <option value="minor">Minor</option>
          </select>
        </div>
      </div>

      {/* NCR List */}
      {!selectedNcr ? (
        <div className="space-y-3 sm:space-y-4">
          {ncrs.map((ncr) => (
            <div
              key={ncr.id}
              onClick={() => setSelectedNcr(ncr)}
              className="bg-white border border-slate-200 rounded-xl p-3 sm:p-6 hover:border-blue-300 cursor-pointer transition-all shadow-sm"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                    <span 
                      className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-medium ${
                        ncr.severity === 'critical' ? 'bg-red-100' :
                        ncr.severity === 'major' ? 'bg-orange-100' :
                        'bg-yellow-100'
                      }`}
                      style={{ 
                        color: ncr.severity === 'critical' ? '#E00702' :
                               ncr.severity === 'major' ? '#FB9703' : '#DFC03D'
                      }}
                    >
                      {ncr.severity?.toUpperCase()}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                      {ncr.vendor_name}
                    </span>
                  </div>
                  <h3 className="font-semibold text-slate-800">{ncr.ncr_number}</h3>
                  <p className="text-slate-600 text-sm line-clamp-2">{ncr.description}</p>
                  <div className="flex items-center gap-4 text-xs text-slate-400 mt-2">
                    <span>Part: {ncr.part_number || 'N/A'}</span>
                    <span>PO: {ncr.po_number || 'N/A'}</span>
                    {ncr.created_at && (
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded ${getAgingColor(getDaysOpen(ncr.created_at))}`}>
                        <Timer className="h-3 w-3" />
                        {getDaysOpen(ncr.created_at)} days
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    ncr.status === 'closed' ? 'bg-green-100 text-green-800' :
                    ncr.status === 'pending_vendor' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {ncr.status?.replace('_', ' ')}
                  </span>
                  {ncr.linked_capa_id && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                      CAPA Linked
                    </span>
                  )}
                  <ChevronRight className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            </div>
          ))}
          {ncrs.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Truck className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>No NCRs found</p>
            </div>
          )}
        </div>
      ) : (
        // NCR Detail View
        <div className="space-y-6">
          <button
            onClick={() => { setSelectedNcr(null); setIsEditing(false); }}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
          >
            <ChevronRight className="h-4 w-4 rotate-180" />
            Back to List
          </button>

          <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span 
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      selectedNcr.severity === 'critical' ? 'bg-red-100' :
                      selectedNcr.severity === 'major' ? 'bg-orange-100' :
                      'bg-yellow-100'
                    }`}
                    style={{ 
                      color: selectedNcr.severity === 'critical' ? '#E00702' :
                             selectedNcr.severity === 'major' ? '#FB9703' : '#DFC03D'
                    }}
                  >
                    {selectedNcr.severity?.toUpperCase()}
                  </span>
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                    {selectedNcr.vendor_name}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-slate-800">{selectedNcr.ncr_number}</h2>
              </div>
              <div className="flex items-center gap-3">
                {selectedNcr.status !== 'closed' && (
                  isEditing ? (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveEdit}
                        disabled={saving}
                        className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        <Save className="h-3 w-3" />
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-sm hover:bg-gray-50"
                      >
                        <X className="h-3 w-3" />
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-1 px-3 py-1.5 border border-blue-600 text-blue-600 rounded text-sm hover:bg-blue-50"
                      data-testid="edit-ncr-btn"
                    >
                      <Edit2 className="h-3 w-3" />
                      Edit
                    </button>
                  )
                )}
                <span className={`px-4 py-2 rounded-full text-sm font-medium ${
                  selectedNcr.status === 'closed' ? 'bg-green-100 text-green-800' :
                  'bg-red-100 text-red-700'
                }`}>
                  {selectedNcr.status?.replace('_', ' ')}
                </span>
              </div>
            </div>

            {isEditing ? (
              /* Edit Mode */
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-slate-500 block mb-1">Severity</label>
                    <select
                      value={editForm.severity}
                      onChange={(e) => setEditForm({...editForm, severity: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="minor">Minor</option>
                      <option value="major">Major</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-slate-500 block mb-1">PO Number</label>
                    <input
                      type="text"
                      value={editForm.po_number}
                      onChange={(e) => setEditForm({...editForm, po_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-500 block mb-1">Part Number</label>
                    <input
                      type="text"
                      value={editForm.part_number}
                      onChange={(e) => setEditForm({...editForm, part_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-500 block mb-1">Quantity Received</label>
                    <input
                      type="number"
                      value={editForm.quantity_received}
                      onChange={(e) => setEditForm({...editForm, quantity_received: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-500 block mb-1">Quantity Defective</label>
                    <input
                      type="number"
                      value={editForm.quantity_defective}
                      onChange={(e) => setEditForm({...editForm, quantity_defective: parseInt(e.target.value) || 0})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-slate-500 block mb-1">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-500 block mb-1">Defect Type</label>
                  <input
                    type="text"
                    value={editForm.defect_type}
                    onChange={(e) => setEditForm({...editForm, defect_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-500 block mb-1">Root Cause</label>
                  <textarea
                    value={editForm.root_cause}
                    onChange={(e) => setEditForm({...editForm, root_cause: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="text-sm text-slate-500 block mb-1">Corrective Action</label>
                  <textarea
                    value={editForm.corrective_action}
                    onChange={(e) => setEditForm({...editForm, corrective_action: e.target.value})}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            ) : (
              /* View Mode */
              <>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">PO Number</p>
                    <p className="font-medium">{selectedNcr.po_number || 'N/A'}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Part Number</p>
                    <p className="font-medium">{selectedNcr.part_number || 'N/A'}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-lg">
                    <p className="text-sm text-slate-500">Quantity Defective</p>
                    <p className="font-medium">{selectedNcr.quantity_defective} / {selectedNcr.quantity_received}</p>
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-slate-500 mb-1">Description</p>
                  <p className="text-slate-700">{selectedNcr.description}</p>
                </div>
              </>
            )}

            {/* CAPA Actions */}
            <div className="flex gap-3 mb-6 mt-4">
              {!selectedNcr.linked_capa_id && (
                <button
                  onClick={() => createCapa(selectedNcr)}
                  className="flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
                >
                  <Plus className="h-4 w-4" />
                  Create CAPA
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New NCR Modal */}
      {showNewModal && (
        <NewNCRModal
          vendors={vendors}
          onClose={() => setShowNewModal(false)}
          onCreated={() => {
            fetchNcrs();
            setShowNewModal(false);
          }}
        />
      )}
    </div>
  );
};

// New NCR Modal Component
const NewNCRModal = ({ vendors, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    ncr_number: '',
    vendor_id: '',
    vendor_name: '',
    po_number: '',
    part_number: '',
    part_description: '',
    quantity_received: 0,
    quantity_defective: 0,
    defect_type: 'dimensional',
    severity: 'major',
    description: '',
    evidence: '',
    disposition: 'pending',
    cost_impact: 0
  });
  const [saving, setSaving] = useState(false);

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setFormData({
      ...formData,
      vendor_id: vendorId,
      vendor_name: vendor?.company_name || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiClient.post('/ncrs', {
        ...formData,
        created_by: 'current-user',
        created_by_name: 'Current User'
      });
      onCreated();
    } catch (error) {
      console.error('Error creating NCR:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-slate-800">New Vendor Non-Conformance</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Vendor</label>
              <select
                required
                value={formData.vendor_id}
                onChange={(e) => handleVendorChange(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="">Select Vendor</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.id}>{vendor.vendor_code} - {vendor.company_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Severity</label>
              <select
                value={formData.severity}
                onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="critical">Critical</option>
                <option value="major">Major</option>
                <option value="minor">Minor</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">PO Number</label>
              <input
                type="text"
                value={formData.po_number}
                onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Part Number</label>
              <input
                type="text"
                value={formData.part_number}
                onChange={(e) => setFormData({ ...formData, part_number: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Qty Received</label>
              <input
                type="number"
                value={formData.quantity_received}
                onChange={(e) => setFormData({ ...formData, quantity_received: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Qty Defective</label>
              <input
                type="number"
                value={formData.quantity_defective}
                onChange={(e) => setFormData({ ...formData, quantity_defective: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Defect Type</label>
              <select
                value={formData.defect_type}
                onChange={(e) => setFormData({ ...formData, defect_type: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              >
                <option value="dimensional">Dimensional</option>
                <option value="material">Material</option>
                <option value="documentation">Documentation</option>
                <option value="packaging">Packaging</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg"
              placeholder="Describe the non-conformance in detail..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create NCR'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NCRManagement;
